// =======================
// zoom.js (Buddy😎 Edition)
// - Desktop : click → zoom follows mouse, mouseleave → close
// - Mobile  : tap → zoom at tap point
//             single-finger drag → delta pan (correct direction)
//             two-finger pinch   → smooth zoom anchored at pinch center
//             tap overlay        → close
// - Swiper touch is disabled while overlay is open
// =======================

(function () {
  "use strict";

  let overlay = null;

  const MIN_ZOOM = 1.2, MAX_ZOOM = 6;

  // ── Swiper ──────────────────────────────────────────────────────────────────

  function getSwiperInstance(image) {
    return image.closest(".swiper")?.swiper || null;
  }

  function disableSwiperTouch(swiper) { if (swiper) swiper.allowTouchMove = false; }
  function enableSwiperTouch(swiper)  { if (swiper) swiper.allowTouchMove = true;  }

  // ── Overlay creation ─────────────────────────────────────────────────────────

  function createOverlay(image) {
    const img = document.createElement("img");
    img.src = image.currentSrc || image.src;

    overlay = document.createElement("div");
    overlay.className = "image-magnify-full-size";
    overlay.setAttribute("aria-hidden", "true");
    overlay.style.backgroundImage    = `url('${img.src}')`;
    overlay.style.backgroundColor    = "var(--gradient-background)";

    image.style.opacity = "50%";
    toggleSpinner(image, true);

    img.onload = () => {
      toggleSpinner(image, false);
      image.parentElement.insertBefore(overlay, image);
      image.style.opacity = "100%";
    };

    return overlay;
  }

  function toggleSpinner(image, show) {
    const s = image.parentElement?.parentElement?.querySelector(".loading__spinner");
    if (s) s.classList.toggle("hidden", !show);
  }

  // ── Helpers ──────────────────────────────────────────────────────────────────

  function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }

  function pinchDist(touches) {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  // Apply bg state to overlay
  function applyBg(el, bgX, bgY, bgW) {
    el.style.backgroundPosition = `${bgX}% ${bgY}%`;
    el.style.backgroundSize     = `${bgW}px`;
  }

  // Read current bgX/bgY from overlay's inline style → store on element
  function syncState(el) {
    const parts = (el.style.backgroundPosition || "50% 50%").split(" ");
    el._bgX = parseFloat(parts[0]) || 50;
    el._bgY = parseFloat(parts[1]) || 50;
  }

  // bg pixel offset at container's left/top edge (pixels from bg origin)
  //   offsetX = extraW × (bgX / 100)
  // bg pixel at container position px:
  //   bgPixel = offsetX + px
  function bgOffset(bgPercent, extra) {
    return (bgPercent / 100) * extra;
  }

  // ── Desktop move ─────────────────────────────────────────────────────────────

  function moveDesktop(image, event, zoom) {
    if (!overlay) return;
    const ratio = image.height / image.width;
    const rect  = image.getBoundingClientRect();
    const xPos  = event.clientX - rect.left;
    const yPos  = event.clientY - rect.top;
    overlay.style.backgroundPosition =
      `${xPos / (image.clientWidth / 100)}% ${yPos / ((image.clientWidth * ratio) / 100)}%`;
    overlay.style.backgroundSize = `${image.width * zoom}px`;
  }

  // ── Mobile initial position (at tap point) ───────────────────────────────────

  function setTapPosition(image, clientX, clientY, zoom) {
    if (!overlay) return;
    const ratio = image.naturalHeight / image.naturalWidth || 1;
    const rect  = image.getBoundingClientRect();
    const cW    = image.clientWidth;
    const cH    = image.clientHeight;
    const bgW   = cW * zoom;
    const bgH   = bgW * ratio;
    const exW   = Math.max(0, bgW - cW);
    const exH   = Math.max(0, bgH - cH);
    const xPos  = clientX - rect.left;
    const yPos  = clientY - rect.top;
    const bgX   = exW > 0 ? clamp(xPos / (cW / 100), 0, 100) : 50;
    const bgY   = exH > 0 ? clamp(yPos / (cH / 100), 0, 100) : 50;
    applyBg(overlay, bgX, bgY, bgW);
  }

  // ── Mobile magnify ───────────────────────────────────────────────────────────

  function magnifyMobile(image, zoom, swiper) {
    const overlayEl = createOverlay(image);

    // Live state
    overlayEl._bgX  = 50;
    overlayEl._bgY  = 50;
    overlayEl._zoom = zoom;
    overlayEl._bgW  = image.clientWidth * zoom;
    overlayEl._didPinch = false;

    // GPU compositing hint — avoids repaint jank during pinch/pan
    overlayEl.style.willChange = "background-position, background-size";

    // Single-finger pan state
    let lastX = 0, lastY = 0;

    // Pinch state (captured at gesture start)
    let isPinching         = false;
    let pinchStartDist0    = 0;
    let pinchStartTime     = 0;
    let pinchStartZoom     = zoom;
    let pinchStartBgW      = 0;
    let pinchStartBgH      = 0;
    let pinchStartExW      = 0;
    let pinchStartExH      = 0;
    let pinchStartOffX     = 0;   // bgOffset at pinch start
    let pinchStartOffY     = 0;
    let pinchRelX          = 0;   // pinch center relative to overlay
    let pinchRelY          = 0;

    // ── rAF batching: coalesce touchmove bursts into one paint per frame ──
    let rafId = null;
    function scheduleApply() {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        if (!overlay) return;
        applyBg(overlay, overlayEl._bgX, overlayEl._bgY, overlayEl._bgW);
      });
    }

    function closeOverlay() {
      if (rafId !== null) { cancelAnimationFrame(rafId); rafId = null; }
      overlayEl.remove();
      overlay = null;
      enableSwiperTouch(swiper);
    }

    // ── touch handlers (named so they can be primed externally for a
    //    pinch gesture that begins directly on the <img>, before the
    //    overlay exists) ──────────────────────────────────────────
    function handleTouchStart(e) {
      // _bgX/_bgY/_bgW are kept authoritative on overlayEl across moves,
      // so re-reading from el.style here would risk a stale value if a
      // scheduled rAF write hasn't applied yet.

      if (e.touches.length >= 2) {
        // Begin pinch
        isPinching          = true;
        overlayEl._didPinch = true;

        const ratio = image.naturalHeight / image.naturalWidth || 1;
        const cW    = image.clientWidth;
        const cH    = image.clientHeight;

        pinchStartDist0  = Math.max(pinchDist(e.touches), 1);
        pinchStartTime   = performance.now();
        pinchStartZoom   = overlayEl._zoom;
        pinchStartBgW    = cW * pinchStartZoom;
        pinchStartBgH    = pinchStartBgW * ratio;
        pinchStartExW    = Math.max(0, pinchStartBgW - cW);
        pinchStartExH    = Math.max(0, pinchStartBgH - cH);
        pinchStartOffX   = bgOffset(overlayEl._bgX, pinchStartExW);
        pinchStartOffY   = bgOffset(overlayEl._bgY, pinchStartExH);

        // Pinch center relative to the image (overlay sits exactly over it)
        const rect = image.getBoundingClientRect();
        const cx   = (e.touches[0].clientX + e.touches[1].clientX) / 2;
        const cy   = (e.touches[0].clientY + e.touches[1].clientY) / 2;
        pinchRelX  = cx - rect.left;
        pinchRelY  = cy - rect.top;

      } else if (e.touches.length === 1 && !isPinching) {
        lastX = e.touches[0].clientX;
        lastY = e.touches[0].clientY;
      }
    }

    function handleTouchMove(e) {
      e.preventDefault();
      if (!overlay) return;

      const ratio = image.naturalHeight / image.naturalWidth || 1;
      const cW    = image.clientWidth;
      const cH    = image.clientHeight;

      if (e.touches.length >= 2 && isPinching) {
        // ── Pinch-to-zoom ──────────────────────────────────────
        //
        // Correct anchor formula (preserves original-image point under pinch center):
        //   scale    = currentDist / startDist
        //   newBgW   = startBgW × scale  (clamped)
        //   newOffX  = (startOffX + pinchRelX) × (newBgW / startBgW) − pinchRelX
        //   newBgX%  = clamp(newOffX / newExtraW × 100, 0, 100)
        //
        const dist  = pinchDist(e.touches);
        const scale = dist / pinchStartDist0;
        const newZoom = clamp(pinchStartZoom * scale, MIN_ZOOM, MAX_ZOOM);

        // Pinched in below minimum zoom → close the magnify overlay.
        // Touch-distance samples are noisy for the first ~100ms of a
        // gesture (fingers still settling), which used to make `scale`
        // read as a false shrink on the very first touchmove tick and
        // instantly close an overlay that had just opened. Gating on
        // elapsed time (rather than only the scale threshold) lets that
        // initial jitter pass without affecting a genuine, sustained
        // pinch-in close gesture.
        const pinchElapsed = performance.now() - pinchStartTime;
        if (pinchElapsed > 100 && scale < 0.98 && newZoom <= MIN_ZOOM) {
          closeOverlay();
          return;
        }

        const newBgW  = cW * newZoom;
        const newBgH  = newBgW * ratio;
        const newExW  = Math.max(0, newBgW - cW);
        const newExH  = Math.max(0, newBgH - cH);

        // Scale factor relative to START of THIS pinch gesture
        const scaleW  = newBgW / pinchStartBgW;
        const scaleH  = newBgH / pinchStartBgH;

        const newOffX = (pinchStartOffX + pinchRelX) * scaleW - pinchRelX;
        const newOffY = (pinchStartOffY + pinchRelY) * scaleH - pinchRelY;

        overlayEl._bgX  = newExW > 0 ? clamp(newOffX / newExW * 100, 0, 100) : 50;
        overlayEl._bgY  = newExH > 0 ? clamp(newOffY / newExH * 100, 0, 100) : 50;
        overlayEl._zoom = newZoom;
        overlayEl._bgW  = newBgW;

        scheduleApply();

      } else if (e.touches.length === 1 && !isPinching) {
        // ── Single-finger delta pan ────────────────────────────
        //
        // Drag RIGHT → image moves right → we see left side → bgX decreases
        // offsetX = extraW × (bgX/100);  newOffsetX = offsetX − dx
        // newBgX% = newOffsetX / extraW × 100 = bgX − dx/extraW×100
        //
        const dx  = e.touches[0].clientX - lastX;
        const dy  = e.touches[0].clientY - lastY;
        lastX = e.touches[0].clientX;
        lastY = e.touches[0].clientY;

        const bgW = cW * overlayEl._zoom;
        const bgH = bgW * ratio;
        const exW = Math.max(0, bgW - cW);
        const exH = Math.max(0, bgH - cH);

        if (exW > 0) overlayEl._bgX = clamp(overlayEl._bgX - (dx / exW) * 100, 0, 100);
        if (exH > 0) overlayEl._bgY = clamp(overlayEl._bgY - (dy / exH) * 100, 0, 100);
        overlayEl._bgW = bgW;

        scheduleApply();
      }
    }

    function handleTouchEnd(e) {
      if (e.touches.length < 2) {
        isPinching = false;
        if (e.touches.length === 1) {
          lastX = e.touches[0].clientX;
          lastY = e.touches[0].clientY;
        }
      }
    }

    overlayEl.addEventListener("touchstart", (e) => { e.stopPropagation(); handleTouchStart(e); }, { passive: true });
    overlayEl.addEventListener("touchmove", (e) => { e.stopPropagation(); handleTouchMove(e); }, { passive: false });
    overlayEl.addEventListener("touchend", (e) => { e.stopPropagation(); handleTouchEnd(e); }, { passive: true });

    // Tap to close (ignore if it followed a pinch gesture)
    overlayEl.addEventListener("click", (e) => {
      e.stopPropagation();
      if (overlayEl._didPinch) { overlayEl._didPinch = false; return; }
      closeOverlay();
    });

    return { closeOverlay, handleTouchStart, handleTouchMove, handleTouchEnd };
  }

  // ── Public API ────────────────────────────────────────────────────────────────

  window.enableZoomOnHover = function (zoomRatio = 2) {
    const isMobile = window.innerWidth < 750;
    const images   = document.querySelectorAll(".image-magnify-hover");

    images.forEach((image) => {
      // Variant changes call enableZoomOnHover() again on the same, reused
      // <img> nodes (see variant_change.js). Without this guard, every call
      // would pile on another set of touch listeners on top of the old
      // ones, which can leave stale/duplicate pinch sessions fighting for
      // the same overlay.
      if (image.dataset.zoomBound === "true") return;
      image.dataset.zoomBound = "true";

      // ── Direct two-finger pinch (no prior tap needed) ──────────────────
      if (isMobile) {
        // A real pinch has to actually move before it counts. Only touching
        // down with two fingers (no spreading yet) must NOT open the zoom —
        // we just neutralize Swiper/native gestures and wait in "pending"
        // state until the fingers move apart/together by a real amount.
        const PINCH_OPEN_THRESHOLD = 0.08; // 8% distance change to confirm intent

        let activePinch  = null;
        let pendingPinch = null; // { dist0, cx, cy }
        let pendingSwiper = null;

        image.addEventListener("touchstart", (e) => {
          if (e.touches.length >= 2 && !overlay && !activePinch) {
            e.preventDefault();
            pendingSwiper = getSwiperInstance(image);
            disableSwiperTouch(pendingSwiper);

            pendingPinch = {
              dist0: Math.max(pinchDist(e.touches), 1),
              cx: (e.touches[0].clientX + e.touches[1].clientX) / 2,
              cy: (e.touches[0].clientY + e.touches[1].clientY) / 2,
            };
          }
        }, { passive: false });

        image.addEventListener("touchmove", (e) => {
          if (activePinch) {
            if (overlay) {
              e.stopPropagation();
              activePinch.handleTouchMove(e);
            } else {
              // Overlay already auto-closed (pinched out past min zoom) —
              // release this gesture back to Swiper so its touch state
              // (isTouched/isMoved) resets cleanly on the upcoming touchend,
              // instead of getting stuck and ignoring the next swipe.
              activePinch = null;
            }
            return;
          }

          if (pendingPinch && e.touches.length >= 2) {
            e.preventDefault();
            const dist   = pinchDist(e.touches);
            const spread = Math.abs(dist / pendingPinch.dist0 - 1);

            if (spread > PINCH_OPEN_THRESHOLD) {
              // Confirmed real pinch — open, anchored at the pinch center
              // (not the image center) so it doesn't visually jump/pan on open.
              activePinch = magnifyMobile(image, MIN_ZOOM, pendingSwiper);
              setTapPosition(image, pendingPinch.cx, pendingPinch.cy, MIN_ZOOM);
              if (overlay) syncState(overlay);
              activePinch.handleTouchStart(e);
              pendingPinch = null;
            }
          }
        }, { passive: false });

        image.addEventListener("touchend", (e) => {
          if (pendingPinch && e.touches.length < 2) {
            // Fingers lifted before a real pinch was ever detected — nothing
            // opened, just hand Swiper back control.
            enableSwiperTouch(pendingSwiper);
            pendingPinch = null;
          }

          if (!activePinch) return;
          if (overlay) {
            e.stopPropagation();
            activePinch.handleTouchEnd(e);
            if (e.touches.length === 0) activePinch = null;
          } else {
            activePinch = null;
          }
        }, { passive: true });

        image.addEventListener("touchcancel", () => {
          if (pendingPinch) {
            enableSwiperTouch(pendingSwiper);
            pendingPinch = null;
          }
          if (activePinch) {
            activePinch.closeOverlay();
            activePinch = null;
          }
        }, { passive: true });
      }

      image.onclick = (event) => {
        const swiper = getSwiperInstance(image);

        // Second tap while open → close
        if (overlay && overlay.parentElement) {
          overlay.remove();
          overlay = null;
          enableSwiperTouch(swiper);
          return;
        }

        disableSwiperTouch(swiper);

        if (isMobile) {
          magnifyMobile(image, zoomRatio, swiper);
          // Set initial zoom position at the tap point, then sync state
          setTapPosition(image, event.clientX, event.clientY, zoomRatio);
          if (overlay) syncState(overlay);
        } else {
          // Desktop: close + mouse tracking in magnify
          const overlayEl = createOverlay(image);
          const closeOverlay = () => {
            overlayEl.remove();
            overlay = null;
            enableSwiperTouch(swiper);
          };
          overlayEl.onclick      = closeOverlay;
          overlayEl.onmousemove  = (e) => moveDesktop(image, e, zoomRatio);
          overlayEl.onmouseleave = closeOverlay;
          moveDesktop(image, event, zoomRatio);
        }
      };
    });
  };

  document.addEventListener("DOMContentLoaded", () => {
    enableZoomOnHover(2);
  });
})();
